"use client";

import { useTheme as useNextThemes } from "next-themes";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { applyThemeToDocument } from "@/lib/themes/apply-theme";
import {
  DEFAULT_THEME_NAME,
  getStoredThemeName,
  migrateLegacyThemeInStorage,
  persistThemeName,
} from "@/lib/themes/storage";
import type { Theme } from "@/lib/themes/types";
import { findThemeByName, themes } from "@/lib/themes/themes";

// ─── Action types (stable callbacks — never recreated after mount) ──────────

type AppThemeActionsContextValue = {
  setTheme: (name: string) => void;
  initTheme: () => void;
  previewTheme: (name: string) => void;
  cancelPreview: () => void;
  toggleMode: () => void;
  /** When true, skips applying stored theme (routes outside `/app` force Zinc only). */
  setMarketingDocumentLock: (locked: boolean) => void;
};

// ─── State types (changes when the active theme changes) ────────────────────

type AppThemeStateContextValue = {
  themes: Theme[];
  currentTheme: Theme;
  currentThemeName: string;
};

// ─── Legacy combined type (kept for backward-compat consumers) ──────────────

type AppThemeContextValue = AppThemeActionsContextValue &
  AppThemeStateContextValue;

// ─── Contexts ───────────────────────────────────────────────────────────────

const AppThemeActionsContext =
  createContext<AppThemeActionsContextValue | null>(null);
const AppThemeStateContext = createContext<AppThemeStateContextValue | null>(
  null
);

// ─── Provider ───────────────────────────────────────────────────────────────

export const AppThemeProvider = ({ children }: { children: ReactNode }) => {
  const { setTheme: setNextTheme } = useNextThemes();

  // Keep next-themes' setter in a ref so syncDocumentAndNext is created once
  // and never needs to be in any dependency array. next-themes guarantees the
  // setter is stable, but using a ref makes that explicit and removes it from
  // the sync-effect's deps, preventing any cascading re-renders.
  const setNextThemeRef = useRef(setNextTheme);
  useEffect(() => {
    setNextThemeRef.current = setNextTheme;
  }, [setNextTheme]);

  const [currentThemeName, setCurrentThemeName] = useState(DEFAULT_THEME_NAME);
  const [mounted, setMounted] = useState(false);

  const isPreviewingRef = useRef(false);
  const marketingDocumentLockRef = useRef(false);
  const lastPreviewThemeNameRef = useRef<string | null>(null);

  // Mirror currentThemeName in a ref so stable action callbacks can always
  // read the latest value without needing it in their dependency arrays.
  const currentThemeNameRef = useRef(currentThemeName);
  useEffect(() => {
    currentThemeNameRef.current = currentThemeName;
  }, [currentThemeName]);

  const currentTheme = useMemo(
    () => findThemeByName(currentThemeName),
    [currentThemeName]
  );

  // Stable: uses refs only, never recreated.
  const syncDocumentAndNext = useCallback((theme: Theme) => {
    applyThemeToDocument(theme);
    setNextThemeRef.current(theme.isDark ? "dark" : "light");
  }, []);

  useEffect(() => {
    migrateLegacyThemeInStorage();
    setCurrentThemeName(getStoredThemeName());
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (isPreviewingRef.current) return;
    if (marketingDocumentLockRef.current) return;

    persistThemeName(currentThemeName);
    syncDocumentAndNext(findThemeByName(currentThemeName));
  }, [currentThemeName, mounted, syncDocumentAndNext]);

  // ── Stable action callbacks ─────────────────────────────────────────────
  // All of these read currentThemeName via currentThemeNameRef so they are
  // created exactly once after mount and never change identity. Components
  // that subscribe only to AppThemeActionsContext will therefore never
  // re-render when the active theme changes.

  const setMarketingDocumentLock = useCallback((locked: boolean) => {
    marketingDocumentLockRef.current = locked;
  }, []);

  const setTheme = useCallback((name: string) => {
    isPreviewingRef.current = false;
    lastPreviewThemeNameRef.current = null;
    // Update the ref immediately so cancelPreview (which reads this ref
    // synchronously in the same flush) sees the new name, not the stale one.
    currentThemeNameRef.current = name;
    setCurrentThemeName(name);
  }, []);

  const initTheme = useCallback(() => {
    syncDocumentAndNext(findThemeByName(currentThemeNameRef.current));
  }, [syncDocumentAndNext]);

  const previewTheme = useCallback((name: string) => {
    if (lastPreviewThemeNameRef.current === name) return;
    lastPreviewThemeNameRef.current = name;
    isPreviewingRef.current = true;
    applyThemeToDocument(findThemeByName(name));
  }, []);

  const cancelPreview = useCallback(() => {
    lastPreviewThemeNameRef.current = null;
    if (!isPreviewingRef.current) return;
    isPreviewingRef.current = false;
    syncDocumentAndNext(findThemeByName(currentThemeNameRef.current));
  }, [syncDocumentAndNext]);

  const toggleMode = useCallback(() => {
    const theme = findThemeByName(currentThemeNameRef.current);
    if (theme.pair) {
      setTheme(theme.pair);
      return;
    }
    setTheme(theme.isDark ? "zinc-light" : "zinc-dark");
  }, [setTheme]);

  // ── Context values ───────────────────────────────────────────────────────

  // Actions value is memoized once: all callbacks are now truly stable
  // (empty deps or only depend on other stable callbacks), so this object
  // is created once at mount and never changes identity.
  const actionsValue = useMemo<AppThemeActionsContextValue>(
    () => ({
      setTheme,
      initTheme,
      previewTheme,
      cancelPreview,
      toggleMode,
      setMarketingDocumentLock,
    }),
    [
      cancelPreview,
      initTheme,
      previewTheme,
      setMarketingDocumentLock,
      setTheme,
      toggleMode,
    ]
  );

  // State value changes only when the active theme changes.
  const stateValue = useMemo<AppThemeStateContextValue>(
    () => ({ themes, currentTheme, currentThemeName }),
    [currentTheme, currentThemeName]
  );

  return (
    <AppThemeActionsContext.Provider value={actionsValue}>
      <AppThemeStateContext.Provider value={stateValue}>
        {children}
      </AppThemeStateContext.Provider>
    </AppThemeActionsContext.Provider>
  );
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Subscribe only to the stable action callbacks.
 * Components using this hook will NOT re-render when the active theme changes.
 */
export const useAppThemeActions = (): AppThemeActionsContextValue => {
  const ctx = useContext(AppThemeActionsContext);
  if (!ctx) {
    throw new Error("useAppThemeActions must be used within AppThemeProvider");
  }
  return ctx;
};

/**
 * Subscribe only to theme state (currentTheme, currentThemeName, themes list).
 * Components using this hook re-render when the active theme changes — and only then.
 */
export const useAppThemeState = (): AppThemeStateContextValue => {
  const ctx = useContext(AppThemeStateContext);
  if (!ctx) {
    throw new Error("useAppThemeState must be used within AppThemeProvider");
  }
  return ctx;
};

/**
 * Combined hook for backward compatibility.
 * Prefer `useAppThemeActions` or `useAppThemeState` when you only need one half,
 * so the component re-renders only when the relevant slice changes.
 */
export const useAppTheme = (): AppThemeContextValue => {
  const actions = useAppThemeActions();
  const state = useAppThemeState();
  // useMemo avoids allocating a new combined object on every render.
  // The component will still re-render when either context changes.
  return useMemo(() => ({ ...actions, ...state }), [actions, state]);
};
